import {QueueListIcon} from "@heroicons/react/24/outline";
import {UserProcess} from "@ots-frontend/ots-data";
import {ElementType, useMemo, useState} from "react";
import {OtsActionButton} from "../components/OtsButton";
import {OtsTray, OtsTrayEmptyState, OtsTrayErrorState, OtsTrayLoadingState} from "../components/OtsTray";
import {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";
import {countProcessKinds, PROCESS_COPY, processKind, sortProcesses, summarizeProcesses} from "./process-ui";
import {ProcessKindFilter, ProcessKindFilterValue} from "./ProcessKindFilter";
import {ProcessRow, ProcessRowAction} from "./ProcessRow";
import {processTrayAccessibleName, ProcessTrayTrigger} from "./ProcessTrayTrigger";

export type ProcessTrayProps = {
  processes: UserProcess[];
  /** First load in progress, with nothing to show yet. */
  loading?: boolean;
  /** Why the list couldn't be loaded. */
  error?: string;
  onReload?: () => void;
  /** Live updates dropped and are reconnecting; the list may be a little behind. */
  liveUpdatesPaused?: boolean;
  /** Actions in flight, by process id. */
  busy?: Record<string, ProcessRowAction | undefined>;
  onCancel?: (process: UserProcess) => void;
  onRetry?: (process: UserProcess) => void;
  onShowDetails?: (process: UserProcess) => void;
  onMarkAllSeen?: () => void;
  onOpenChange?: (open: boolean) => void;
  /** Renders the trigger as another component, e.g. catalyst's NavbarItem. */
  triggerAs?: ElementType;
  iconOnly?: boolean;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** The person's processes behind a navbar trigger that shows whether anything is running or new. */
export const ProcessTray = ({
  processes,
  loading = false,
  error,
  onReload,
  liveUpdatesPaused = false,
  busy = {},
  onCancel,
  onRetry,
  onShowDetails,
  onMarkAllSeen,
  onOpenChange,
  triggerAs,
  iconOnly,
  brand,
  colorScheme,
}: ProcessTrayProps) => {
  const summary = useMemo(() => summarizeProcesses(processes), [processes]);
  const sorted = useMemo(() => sortProcesses(processes), [processes]);
  const kinds = useMemo(() => countProcessKinds(processes), [processes]);
  const [kindFilter, setKindFilter] = useState<ProcessKindFilterValue>("all");

  // the filter only appears once there's more than one kind to tell apart; a kind that empties out falls back to All
  const showKindFilter = kinds.length > 1;
  const activeKind = showKindFilter && kinds.some((info) => info.kind === kindFilter) ? kindFilter : "all";
  const visible = activeKind === "all" ? sorted : sorted.filter((process) => processKind(process) === activeKind);

  return (
    <OtsTray
      trigger={<ProcessTrayTrigger summary={summary} iconOnly={iconOnly} />}
      triggerLabel={processTrayAccessibleName(summary)}
      triggerAs={triggerAs}
      label={PROCESS_COPY.panelTitle}
      brand={brand}
      colorScheme={colorScheme}
      onOpenChange={onOpenChange}
      actions={
        onMarkAllSeen && (
          <OtsActionButton size="compact" variant="text" disabled={summary.unseenCount === 0} onClick={onMarkAllSeen}>
            {PROCESS_COPY.markAllSeen}
          </OtsActionButton>
        )
      }
      toolbar={
        !error && (showKindFilter || liveUpdatesPaused) ? (
          <div className="space-y-2">
            {showKindFilter && (
              <ProcessKindFilter kinds={kinds} total={processes.length} value={activeKind} onChange={setKindFilter} />
            )}
            {liveUpdatesPaused && (
              <p className="text-xs text-[var(--ots-text-muted)]">{PROCESS_COPY.liveUpdatesPaused}</p>
            )}
          </div>
        ) : undefined
      }
    >
      {({close}) =>
        error ? (
          <OtsTrayErrorState title={PROCESS_COPY.loadFailed} onRetry={onReload}>
            {error}
          </OtsTrayErrorState>
        ) : loading && processes.length === 0 ? (
          <OtsTrayLoadingState label="Loading your processes" />
        ) : sorted.length === 0 ? (
          <OtsTrayEmptyState
            title={PROCESS_COPY.emptyTitle}
            description={PROCESS_COPY.emptyDescription}
            icon={<QueueListIcon />}
          />
        ) : (
          <ul className="divide-y divide-[var(--ots-outline-variant)]">
            {visible.map((process) => (
              <li key={process.id}>
                <ProcessRow
                  process={process}
                  busyAction={busy[process.id]}
                  onCancel={onCancel}
                  onRetry={onRetry}
                  onShowDetails={
                    onShowDetails &&
                    ((target) => {
                      close();
                      onShowDetails(target);
                    })
                  }
                />
              </li>
            ))}
          </ul>
        )
      }
    </OtsTray>
  );
};
